import * as THREE from 'three'

import {
  buildMdlMesh,
  computeBoneWorld,
  type MdlBuilt,
} from '@/cs/buildMdlMesh'
import { parseMdl, sampleBoneLocals, type Mdl } from '@/cs/mdl'
import type { ArmouryPickup } from '@/cs/buildBspMesh'
import type { WeaponKind } from './Weapon'

interface GroundWeapon {
  kind: WeaponKind
  group: THREE.Group
  source: 'map' | 'drop'
  position: THREE.Vector3
  velocity: THREE.Vector3
  yaw: number
  picked: boolean
  armed: boolean
  pickupable: boolean
  availableAt: number
  // Rounds this floor item grants when grabbed. Map spawns leave these
  // undefined (the picker grants a full loadout); dropped weapons carry the
  // clip/reserve they had when dropped.
  ammo?: number
  reserve?: number
}

/** What the picker is told about a floor item it grabbed. */
export interface GroundPickupInfo {
  kind: WeaponKind
  source: 'map' | 'drop'
  ammo?: number
  reserve?: number
}

export interface DroppedWeaponOptions {
  velocity?: THREE.Vector3
  delayMs?: number
  armAfterExit?: boolean
  pickupable?: boolean
  ammo?: number
  reserve?: number
}

const WORLD_MODEL_URLS: Partial<Record<WeaponKind, string>> = {
  ak: '/cs/models/w_ak47.mdl',
  mp5: '/cs/models/w_mp5.mdl',
  tmp: '/cs/models/w_tmp.mdl',
  p90: '/cs/models/w_p90.mdl',
  mac10: '/cs/models/w_mac10.mdl',
  m4a1: '/cs/models/w_m4a1.mdl',
  m3: '/cs/models/w_m3.mdl',
  xm1014: '/cs/models/w_xm1014.mdl',
  m249: '/cs/models/w_m249.mdl',
  glock: '/cs/models/w_glock18.mdl',
  usp: '/cs/models/w_usp.mdl',
  deagle: '/cs/models/w_deagle.mdl',
  p228: '/cs/models/w_p228.mdl',
  elite: '/cs/models/w_elite.mdl',
  fiveseven: '/cs/models/w_fiveseven.mdl',
}

const PICKUP_RADIUS = 34
const GRAVITY = 800
const FLOOR_BOUNCE = 0.22
const FLOOR_FRICTION = 7.5
const SLEEP_SPEED = 8
type FloorSnap = (position: THREE.Vector3, dynamic?: boolean) => THREE.Vector3

export default class GroundWeapons {
  public onPickup?: (info: GroundPickupInfo) => boolean

  private readonly root = new THREE.Group()
  private readonly pickups: GroundWeapon[] = []
  private readonly prototypePromises = new Map<WeaponKind, Promise<THREE.Group>>()
  private readonly prototypes: THREE.Group[] = []
  private source?: ArmouryPickup[]
  private readonly box = new THREE.Box3()
  private readonly center = new THREE.Vector3()

  constructor(
    private readonly scene: THREE.Scene,
    private readonly snapToFloor: FloorSnap = (position) => position.clone(),
  ) {
    this.root.name = 'ground-weapons'
    this.scene.add(this.root)
  }

  public setPickups(source: ArmouryPickup[]): void {
    if (this.source === source) return
    this.clear()
    this.source = source
    for (const pickup of source) {
      this.spawnPickup(pickup.kind as WeaponKind, pickup.position, source)
    }
  }

  public addDropped(
    kind: WeaponKind,
    position: THREE.Vector3,
    yaw = hashYaw(position),
    options: DroppedWeaponOptions = {},
  ): void {
    this.spawnPickup(kind, position, {
      source: 'drop',
      yaw,
      velocity: options.velocity,
      delayMs: options.delayMs ?? 700,
      armAfterExit: options.armAfterExit ?? true,
      pickupable: options.pickupable ?? true,
      snap: false,
      ammo: options.ammo,
      reserve: options.reserve,
    })
  }

  public update(playerFeet: THREE.Vector3, dt: number): void {
    const now = performance.now()
    for (const pickup of this.pickups) {
      if (pickup.picked) continue
      this.updatePhysics(pickup, dt)
      if (!pickup.pickupable) continue
      if (pickup.availableAt > now) continue
      const dx = pickup.group.position.x - playerFeet.x
      const dz = pickup.group.position.z - playerFeet.z
      const dy = pickup.group.position.y - playerFeet.y
      const distSq = dx * dx + dz * dz
      if (!pickup.armed && distSq > (PICKUP_RADIUS * 1.35) ** 2) {
        pickup.armed = true
      }
      if (!pickup.armed) continue
      if (distSq > PICKUP_RADIUS * PICKUP_RADIUS) continue
      if (Math.abs(dy) > 74) continue
      const accepted =
        this.onPickup?.({
          kind: pickup.kind,
          source: pickup.source,
          ammo: pickup.ammo,
          reserve: pickup.reserve,
        }) ?? false
      pickup.availableAt = now + 700
      if (!accepted) continue
      // Map weapons are permanent spawners — they stay. A dropped weapon is a
      // real item: once grabbed it's consumed and removed from the world.
      if (pickup.source === 'map') continue
      pickup.picked = true
      pickup.group.removeFromParent()
    }
  }

  private buildPlaceholder(kind: WeaponKind, position: THREE.Vector3): THREE.Group {
    const group = new THREE.Group()
    group.name = `pickup:${kind}`
    group.rotation.y = hashYaw(position)
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(26, 4, 9),
      new THREE.MeshStandardMaterial({
        color: 0x2d3238,
        roughness: 0.72,
        metalness: 0.08,
      }),
    )
    mesh.castShadow = false
    mesh.receiveShadow = false
    group.add(mesh)
    this.placeOnGround(group, position, group.rotation.y)
    this.markNonSolid(group)
    return group
  }

  private spawnPickup(
    kind: WeaponKind,
    position: THREE.Vector3,
    options:
      | ArmouryPickup[]
      | {
          source: 'map' | 'drop'
          yaw?: number
          velocity?: THREE.Vector3
          delayMs?: number
          armAfterExit?: boolean
          pickupable?: boolean
          snap?: boolean
          ammo?: number
          reserve?: number
        },
  ): void {
    const isMapSource = Array.isArray(options)
    const source = isMapSource ? 'map' : options.source
    const yaw = isMapSource ? hashYaw(position) : (options.yaw ?? hashYaw(position))
    const floorPosition =
      isMapSource || options.snap !== false ? this.snapToFloor(position) : position.clone()
    const placeholder = this.buildPlaceholder(kind, floorPosition)
    placeholder.rotation.y = yaw
    this.placeOnGround(placeholder, floorPosition, yaw)
    this.root.add(placeholder)
    const entry: GroundWeapon = {
      kind,
      group: placeholder,
      source,
      position: floorPosition.clone(),
      velocity:
        !isMapSource && options.velocity
          ? options.velocity.clone()
          : new THREE.Vector3(),
      yaw,
      picked: false,
      armed: isMapSource || options.armAfterExit !== true,
      pickupable: isMapSource || options.pickupable !== false,
      availableAt: performance.now() + (isMapSource ? 0 : (options.delayMs ?? 0)),
      ammo: isMapSource ? undefined : options.ammo,
      reserve: isMapSource ? undefined : options.reserve,
    }
    this.pickups.push(entry)
    void this.loadWorldModel(kind)
      .then((prototype) => {
        if (entry.picked || (isMapSource && this.source !== options)) return
        const model = prototype.clone(true)
        this.markNonSolid(model)
        this.placeOnGround(model, entry.position, yaw)
        this.root.remove(placeholder)
        this.root.add(model)
        entry.group = model
      })
      .catch((error) => {
        console.warn(`[cs] pickup model failed to load (${kind}):`, error)
      })
  }

  private updatePhysics(pickup: GroundWeapon, dt: number): void {
    if (pickup.source !== 'drop' || pickup.velocity.lengthSq() <= 0) return
    const step = Math.min(dt, 0.05)
    pickup.velocity.y -= GRAVITY * step
    pickup.position.addScaledVector(pickup.velocity, step)
    pickup.yaw += pickup.velocity.length() * step * 0.015

    const floor = this.snapToFloor(pickup.position, true)
    if (pickup.position.y <= floor.y) {
      pickup.position.y = floor.y
      if (Math.abs(pickup.velocity.y) > 80) {
        pickup.velocity.y = -pickup.velocity.y * FLOOR_BOUNCE
      } else {
        pickup.velocity.y = 0
      }
      const damp = Math.max(0, 1 - FLOOR_FRICTION * step)
      pickup.velocity.x *= damp
      pickup.velocity.z *= damp
      if (pickup.velocity.length() < SLEEP_SPEED) pickup.velocity.set(0, 0, 0)
    }

    this.placeOnGround(pickup.group, pickup.position, pickup.yaw)
  }

  private async loadWorldModel(kind: WeaponKind): Promise<THREE.Group> {
    const existing = this.prototypePromises.get(kind)
    if (existing) return existing
    const promise = this.buildWorldModel(kind)
    this.prototypePromises.set(kind, promise)
    return promise
  }

  private async buildWorldModel(kind: WeaponKind): Promise<THREE.Group> {
    const url = WORLD_MODEL_URLS[kind]
    if (!url) throw new Error(`No world model for ${kind}`)
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const mdl: Mdl = parseMdl(await res.arrayBuffer())
    const built: MdlBuilt = buildMdlMesh(mdl)
    built.skin(computeBoneWorld(mdl, sampleBoneLocals(mdl, -1, 0)))
    this.markNonSolid(built.group)
    this.prototypes.push(built.group)
    return built.group
  }

  private placeOnGround(
    group: THREE.Group,
    position: THREE.Vector3,
    yaw: number,
  ): void {
    group.position.copy(position)
    group.rotation.y = yaw
    group.updateMatrixWorld(true)
    this.box.setFromObject(group)
    if (!Number.isFinite(this.box.min.y)) return
    this.box.getCenter(this.center)
    group.position.x += position.x - this.center.x
    group.position.z += position.z - this.center.z
    group.updateMatrixWorld(true)
    this.box.setFromObject(group)
    group.position.y += position.y + 1 - this.box.min.y
  }

  private markNonSolid(object: THREE.Object3D): void {
    object.userData.nonSolid = true
    object.traverse((child) => {
      child.userData.nonSolid = true
    })
  }

  private clear(): void {
    for (const pickup of this.pickups) pickup.group.removeFromParent()
    this.pickups.length = 0
    this.root.clear()
  }

  public dispose(): void {
    this.clear()
    this.root.removeFromParent()
    for (const prototype of this.prototypes) disposeObject(prototype)
    this.prototypes.length = 0
    this.prototypePromises.clear()
  }
}

function hashYaw(position: THREE.Vector3): number {
  return Math.sin(position.x * 12.9898 + position.z * 78.233) * Math.PI
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return
    child.geometry.dispose()
    const material = child.material as THREE.Material | THREE.Material[]
    const materials = Array.isArray(material) ? material : [material]
    for (const mat of materials) {
      for (const value of Object.values(mat)) {
        if (value instanceof THREE.Texture) value.dispose()
      }
      mat.dispose()
    }
  })
}
